/**
 * 사이드바 공통 기능 유틸리티
 *
 * React 모듈로 변환된 버전
 * 하위 호환성을 위해 window.sidebarCommon으로도 노출됩니다.
 */

// 사이드바 공통 객체
export const SidebarCommon = {
  contentArea: null,
  currentComponent: null,
  currentType: 'simple',
  initialized: false,

  init: function() {
    // 이미 초기화되었으면 스킵
    if (this.initialized && this.contentArea) {
      return;
    }

    this.contentArea = document.getElementById('sidebar-common-content-area');
    if (!this.contentArea) {
      console.warn('sidebar-common-content-area 요소를 찾을 수 없습니다.');
      return;
    }

    // 저장된 사이드바 유형 읽기 (ThemeConfig 사용)
    try {
      if (typeof window !== 'undefined' && window.ThemeConfig) {
        const savedType = window.ThemeConfig.get('rightSidebarType');
        if (savedType) {
          this.currentType = savedType;
        }
      }
    } catch (e) {
      console.warn('ThemeConfig 읽기 오류:', e);
    }

    // 저장된 유형으로 컨텐츠 로드
    this.loadSidebarType(this.currentType);

    // 내부 컴포넌트 로드 (sidebar-common-list.html)
    //this.loadContent('html', '/assets/templates/sidebar-common-list.html');

    this.initialized = true;
  },

  loadSidebarType: function(type) {
    // 사이드바 외부 구조 요소 찾기 (재시도 로직 추가)
    const self = this;
    const findAndApplyType = () => {
      const sidebarElement = document.querySelector('.sidebar-common-active');
      if (!sidebarElement) {
        return false;
      }

      // 사이드바가 열려있는지 확인 (inside 클래스 보존)
      const isOpen = sidebarElement.classList.contains('inside');
      const wrapper = document.querySelector('.main-wrapper');

      self.currentType = type;

      // 기존 유형 클래스 제거
      sidebarElement.classList.remove('sidebar-type-simple', 'sidebar-type-modern', 'sidebar-type-vibrant');

      // 새로운 유형 클래스 추가 (외부 구조 스타일만 변경)
      switch(type) {
        case 'modern':
          sidebarElement.classList.add('sidebar-type-modern');
          break;
        case 'vibrant':
          sidebarElement.classList.add('sidebar-type-vibrant');
          break;
        case 'simple':
        default:
          sidebarElement.classList.add('sidebar-type-simple');
          break;
      }

      // 사이드바가 열려있었으면 상태 유지
      if (isOpen) {
        sidebarElement.classList.add('inside');
        if (wrapper) {
          wrapper.classList.add('overlay-active');
        }
      }

      return true;
    };

    // 즉시 시도
    if (findAndApplyType()) {
      return;
    }

    // 요소를 찾지 못한 경우 재시도 (React Portal로 렌더링되는 경우 대비)
    const maxRetries = 30; // 3초 (30 * 100ms)
    let retryCount = 0;

    const retry = () => {
      if (findAndApplyType()) {
        return; // 성공
      }

      if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(retry, 100);
      } else {
        // 최대 재시도 횟수 초과 시 경고 (조용히 실패)
        // console.warn('sidebar-common-active 요소를 찾을 수 없습니다.');
      }
    };

    setTimeout(retry, 100);
  },

  loadDefaultContent: function() {
    if (!this.contentArea) return;

    // 기본 메시지 표시 (내부 컨텐츠가 없을 때만)
    this.contentArea.innerHTML = `
      <div class="text-center text-muted py-5">
        <p>사이드바 컨텐츠 영역</p>
        <p class="small">컨텐츠를 로드하려면 loadContent() 함수를 호출하세요.</p>
      </div>
    `;
  },

  loadContent: function(componentType, componentPath, data) {
    if (!this.contentArea) {
      console.warn('sidebar-common-content-area 요소를 찾을 수 없습니다.');
      return;
    }

    // 로딩 표시
    this.contentArea.innerHTML = `
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-muted">컨텐츠를 불러오는 중...</p>
      </div>
    `;

    // 컴포넌트 타입에 따라 다른 처리
    if (componentType === 'html' && componentPath) {
      // HTML 파일을 동적으로 로드
      this.loadHtmlComponent(componentPath, data);
    } else if (componentType === 'fragment' && componentPath) {
      // Thymeleaf fragment를 동적으로 로드
      this.loadFragment(componentPath, data);
    } else {
      // 기본 컨텐츠 표시
      this.loadDefaultContent();
    }
  },

  loadHtmlComponent: function(componentPath, data) {
    const self = this;
    // HTML 파일을 fetch로 로드
    fetch(componentPath)
      .then(response => {
        if (!response.ok) {
          throw new Error('컨텐츠를 불러올 수 없습니다.');
        }
        return response.text();
      })
      .then(html => {
        if (self.contentArea) {
          self.contentArea.innerHTML = html;
          // 스크립트 실행 (필요한 경우)
          self.executeScripts(self.contentArea);
        }
      })
      .catch(error => {
        console.error('컨텐츠 로드 오류:', error);
        if (self.contentArea) {
          self.contentArea.innerHTML = `
            <div class="text-center text-danger py-5">
              <p>컨텐츠를 불러올 수 없습니다.</p>
              <p class="small">${error.message}</p>
            </div>
          `;
        }
      });
  },

  loadFragment: function(fragmentPath, data) {
    // Thymeleaf fragment는 서버 사이드 렌더링이 필요하므로
    // 여기서는 기본 메시지만 표시
    if (this.contentArea) {
      this.contentArea.innerHTML = `
        <div class="text-center text-muted py-5">
          <p>Fragment: ${fragmentPath}</p>
          <p class="small">Fragment는 서버 사이드에서 렌더링되어야 합니다.</p>
        </div>
      `;
    }
  },

  executeScripts: function(container) {
    // 동적으로 로드된 HTML 내의 스크립트 실행
    const scripts = container.querySelectorAll('script');
    scripts.forEach(function(oldScript) {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(function(attr) {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  },

  clearContent: function() {
    if (this.contentArea) {
      this.contentArea.innerHTML = '';
    }
  }
};

// 하위 호환성을 위해 window.sidebarCommon으로 노출
if (typeof window !== 'undefined') {
  window.sidebarCommon = SidebarCommon;
}

